/**
 * Monthly vs. annual billing-period helpers for subscription plans (ticket: annual plans for
 * Starter/Premium/Professional, backend attribute tracked separately under #2487/#2489).
 *
 * `subscriptionType.billing` already exists (currently 'daily' for the free Professional trial,
 * otherwise implicitly monthly - see the "täglich"/"Monat" price-suffix checks that read it
 * directly) and old, unreachable 2021-era mock data in signup/Signup.js used exactly this field
 * with 'annually'/'monthly' for the same purpose, so that's the field this reads rather than
 * inventing a new one. `subscriptionBilling` is the flattened equivalent on the
 * "currentSubscription"/"nextSubscription" view-model objects built in Main.js/Dashboard.js/
 * YourSubscription.js.
 *
 * ASSUMPTION (documented here because the backend doesn't send an annual/monthly value yet at
 * the time this was written): once #2487 ships, `billing` is expected to also carry 'annual'
 * alongside its existing 'daily' value. Annual plans are renewed and invoiced once a year for
 * the full amount (per the ticket), so `price` on an annual subscription type is taken to be the
 * yearly total, not a monthly figure. Anything that isn't recognized as annual (including
 * 'daily', and everything before #2487 ships) is treated as monthly, so nothing about the
 * existing plans changes until the backend value is actually present.
 *
 * If the real value ends up shaped differently, this file is the only place that needs to
 * change - every screen that shows plan pricing (NewSubscription, PaymentConfirmationModal)
 * goes through these helpers rather than reading `billing`/`price` directly.
 */
import { formatNumber } from './stringsManipulations';
import { SUBSCRIPTION_CATEGORY, getSubscriptionTier, getSubscriptionTierRank } from './subscriptionFeatureAccess';

export const BILLING_PERIOD = {
  MONTHLY: 'MONTHLY',
  ANNUAL: 'ANNUAL',
};

export const getBillingPeriod = (subscriptionType) =>
  (subscriptionType?.billing || subscriptionType?.subscriptionBilling)?.toString().toUpperCase().startsWith('ANNUAL')
    ? BILLING_PERIOD.ANNUAL
    : BILLING_PERIOD.MONTHLY;

export const isAnnualSubscription = (subscriptionType) =>
  getBillingPeriod(subscriptionType) === BILLING_PERIOD.ANNUAL;

// Raw plans from GET /subscriptions carry `price`; the flattened currentSubscription /
// nextSubscription view-models carry the same figure as `subscriptionAmount`.
const getPlanPrice = (subscription) => Number(subscription?.price ?? subscription?.subscriptionAmount) || 0;

// Headline figure shown on plan cards / the confirmation modal: always the monthly-equivalent
// amount, so all tiers stay visually comparable regardless of which billing period is selected.
export const getMonthlyEquivalentPrice = (subscriptionType) =>
  isAnnualSubscription(subscriptionType) ? getPlanPrice(subscriptionType) / 12 : getPlanPrice(subscriptionType);

// The amount actually invoiced per billing cycle (the yearly total, for annual plans).
export const getYearlyPrice = (subscriptionType) =>
  isAnnualSubscription(subscriptionType) ? getPlanPrice(subscriptionType) : getPlanPrice(subscriptionType) * 12;

// Euro and cent parts for the price cards, which render the cents in a smaller font
// ("19" + "50" + "€"). Always two cent digits: splitting `price.toString()` on the decimal point
// rendered whole-euro prices such as an annual plan's 234 as "234undefined0".
export const splitPriceForDisplay = (price) => {
  const [euros, cents] = (Number(price) || 0).toFixed(2).split('.');
  return { euros, cents };
};

/**
 * % an annual plan saves vs. paying its monthly counterpart for 12 months. The backend doesn't
 * return this - it has to be derived on the frontend from the matching monthly plan's price, so
 * the full `subscriptionTypes` list (from GET /subscriptions) is needed to find that counterpart.
 * Returns null when there's no matching monthly plan to compare against, or there's no savings.
 */
export const getAnnualSavingsPercent = (annualSubscriptionType, subscriptionTypes = []) => {
  if (!isAnnualSubscription(annualSubscriptionType)) return null;

  const monthlyCounterpart = subscriptionTypes.find(
    (subscriptionType) =>
      subscriptionType?.name === annualSubscriptionType?.name && !isAnnualSubscription(subscriptionType)
  );
  if (!monthlyCounterpart?.price) return null;

  const monthlyBilledYearly = monthlyCounterpart.price * 12;
  const annualPrice = getYearlyPrice(annualSubscriptionType);
  if (monthlyBilledYearly <= 0) return null;

  const savings = ((monthlyBilledYearly - annualPrice) / monthlyBilledYearly) * 100;
  return savings > 0 ? savings : null;
};

// "20 %": rounded, with a non-breaking space before the sign, as German texts write it, so the
// number and the sign never end up on separate lines.
export const formatSavingsPercent = (percent) => `${Math.round(percent)}\u00A0%`;

// The pieces of "Jährliche Abrechnung: XX,XX € / Jahr - Sie sparen XX % gegenüber monatlicher
// Abrechnung.", so a page can set the savings part in bold (see components/AnnualBillingSummary).
export const getAnnualBillingSummaryParts = (subscriptionType, subscriptionTypes = []) => {
  const savingsPercent = getAnnualSavingsPercent(subscriptionType, subscriptionTypes);
  return {
    yearly: `Jährliche Abrechnung: ${formatNumber(getYearlyPrice(subscriptionType), true)} / Jahr`,
    savings: savingsPercent !== null ? `Sie sparen ${formatSavingsPercent(savingsPercent)}` : null,
  };
};

// "Jährliche Abrechnung: XX,XX € / Jahr - Sie sparen XX % gegenüber monatlicher Abrechnung."
export const getAnnualBillingSummaryText = (subscriptionType, subscriptionTypes = []) => {
  const { yearly, savings } = getAnnualBillingSummaryParts(subscriptionType, subscriptionTypes);
  return savings ? `${yearly} - ${savings} gegenüber monatlicher Abrechnung.` : `${yearly}.`;
};

// Highest savings among the annual plans, rounded like the plan cards ("bis 20 % sparen" on the
// billing period selector). Null when there is no annual plan with a monthly counterpart.
export const getMaxAnnualSavingsPercent = (subscriptionTypes = []) => {
  const savings = subscriptionTypes
    .filter(isAnnualSubscription)
    .map((subscriptionType) => getAnnualSavingsPercent(subscriptionType, subscriptionTypes))
    .filter((percent) => percent !== null);
  return savings.length ? Math.round(Math.max(...savings)) : null;
};

// "Jährliche Abrechnung" / "Monatliche Abrechnung", the line under a plan's price.
export const getBillingPeriodLabel = (subscription) =>
  isAnnualSubscription(subscription) ? 'Jährliche Abrechnung' : 'Monatliche Abrechnung';

// "(jährliche Abrechnung)" / "(monatliche Abrechnung)", written after a plan's name in running text.
export const getBillingPeriodSuffix = (subscription) =>
  `(${isAnnualSubscription(subscription) ? 'jährliche' : 'monatliche'} Abrechnung)`;

/**
 * Whether switching from `currentSubscription` to `targetSubscriptionType` is a downgrade,
 * covering the annual-plan cases (backend ticket calls these out explicitly - "no exception"):
 *  - monthly -> annual: always an upgrade, regardless of tier.
 *  - annual -> monthly: always a downgrade, regardless of tier.
 *  - annual -> annual: a downgrade only if moving to a lower tier.
 *  - monthly -> monthly: unchanged, existing tier-based behaviour.
 * Tier comparison goes through `category` (#2470, see helpers/subscriptionFeatureAccess.js)
 * rather than the plan's display `name`, so a custom-named plan still ranks correctly by tier.
 * `currentSubscription`/`targetSubscriptionType` are expected to expose `billing`/
 * `subscriptionBilling`, matching the shapes already used across the app.
 */
export const isSubscriptionSwitchADowngrade = (currentSubscription, targetSubscriptionType) => {
  const currentIsAnnual = isAnnualSubscription(currentSubscription);
  const targetIsAnnual = isAnnualSubscription(targetSubscriptionType);

  if (!currentIsAnnual && targetIsAnnual) return false;
  if (currentIsAnnual && !targetIsAnnual) return true;
  if (currentIsAnnual && targetIsAnnual) {
    return getSubscriptionTierRank(targetSubscriptionType) < getSubscriptionTierRank(currentSubscription);
  }

  // monthly -> monthly: pre-existing tier-based rule.
  const currentTier = getSubscriptionTier(currentSubscription);
  const targetTier = getSubscriptionTier(targetSubscriptionType);
  if (
    currentTier === SUBSCRIPTION_CATEGORY.PROFESSIONAL &&
    currentSubscription?.subscriptionKey !== 'PROFESSIONAL_TESTING_12_2024'
  ) {
    return targetTier === SUBSCRIPTION_CATEGORY.STARTER || targetTier === SUBSCRIPTION_CATEGORY.PREMIUM;
  }
  if (currentTier === SUBSCRIPTION_CATEGORY.PREMIUM) {
    return targetTier === SUBSCRIPTION_CATEGORY.STARTER;
  }
  return false;
};
